import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { ChevronDown, ChevronRight, MessageSquarePlus } from "lucide-react";
import { toast } from "sonner";
import { useNewestRecordFlash } from "@/hooks/useNewestRecordFlash";
import { formatTimestamp } from "@/utils/datetime";
import { newestFirst } from "@/utils/sortHistory";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/operations`;
const RECENT_ACTIVITY_COLLAPSED_KEY = "project-recent-activity:collapsed";

const formatTime = (value) => (value ? formatTimestamp(value) : "Just now");

const activityLabel = (activity) =>
  (activity.trigger_type || "project_update").replaceAll("_", " ");

const CommunicationHistoryEntry = ({ isFlashing, item, onManage }) => (
  <article
    className={isFlashing ? "newest-record-flash" : undefined}
    data-testid={`project-communication-entry-${item.id}`}
  >
    <small data-testid={`project-communication-meta-${item.id}`}>
      {item.item_type} · {formatTime(item.created_at)} · {item.visibility}
    </small>
    <strong data-testid={`project-communication-note-${item.id}`}>
      {item.original_note || item.notes || item.title || "Operational update"}
    </strong>
    {item.additional_notes && (
      <p data-testid={`project-communication-additional-notes-${item.id}`}>
        Additional Notes · {item.additional_notes}
      </p>
    )}
    {(item.additional_note_entries || []).map((entry) => (
      <p data-testid={`project-additional-note-${entry.id}`} key={entry.id}>
        Additional Note · {entry.message}
        {entry.author_name_snapshot ? ` · ${entry.author_name_snapshot}` : ""}
      </p>
    ))}
    <span data-testid={`project-communication-author-${item.id}`}>
      {item.created_by_name_snapshot || "Unknown author"}
    </span>
    {item.assigned_user_names?.length > 0 && (
      <span>Shared with {item.assigned_user_names.join(", ")}</span>
    )}
    {item.status === "complete" && (
      <span data-testid={`project-communication-completion-${item.id}`}>
        Completed · {formatTime(item.completed_at)} · {
          item.completed_by_name_snapshot || "Unknown user"
        }
      </span>
    )}
    {item.deleted_at && (
      <span data-testid={`project-communication-archived-${item.id}`}>
        Legacy archive metadata · {formatTime(item.deleted_at)}
        {item.deleted_by_name_snapshot
          ? ` · ${item.deleted_by_name_snapshot}`
          : ""}
      </span>
    )}
    {item.edit_history?.length > 0 && (
      <span>
        {item.edit_history.length} saved edit{item.edit_history.length === 1 ? "" : "s"}
      </span>
    )}
    {onManage && (
      <button
        data-testid={`project-communication-edit-${item.id}`}
        onClick={() => onManage(item)}
        type="button"
      >
        {item.status === "complete" || item.deleted_at ? "View" : "Edit"}
      </button>
    )}
  </article>
);

export const ProjectCommunicationCenter = ({
  canCreateFieldNotes,
  communications,
  permitActions = [],
  onCreated,
  onManage,
  projectId,
  recentActivity,
}) => {
  const [note, setNote] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [activityCollapsed, setActivityCollapsed] = useState(() => {
    try {
      return window.localStorage.getItem(RECENT_ACTIVITY_COLLAPSED_KEY) !== "0";
    } catch {
      return true;
    }
  });
  useEffect(() => {
    try {
      window.localStorage.setItem(
        RECENT_ACTIVITY_COLLAPSED_KEY,
        activityCollapsed ? "1" : "0",
      );
    } catch {
      // storage unavailable — non-blocking
    }
  }, [activityCollapsed]);
  const meaningfulActivity = useMemo(
    () => recentActivity.filter((activity) => activity.trigger_type !== "time_recalculation"),
    [recentActivity],
  );
  const orderedCommunications = useMemo(
    () => newestFirst(communications),
    [communications],
  );
  const flashCommId = useNewestRecordFlash(orderedCommunications);
  const latest = orderedCommunications.slice(0, 3);
  const historical = orderedCommunications.slice(3);

  const saveFieldNote = async (event) => {
    event.preventDefault();
    if (!note.trim()) return;
    setIsSaving(true);
    try {
      await axios.post(`${API}/projects/${projectId}/field-notes`, {
        notes: note.trim(),
      });
      setNote("");
      toast.success("Field note added.");
      await onCreated();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Field note could not be added.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <section
      className="command-panel command-communication-panel"
      data-testid="command-communication"
    >
      <div className="command-panel-heading">
        <div>
          <span className="operations-kicker">Communication</span>
          <h2>Communication History</h2>
        </div>
        {onManage && (
          <button
            data-testid="project-communication-new-button"
            onClick={() => onManage(null)}
            type="button"
          >
            New Task / Note
          </button>
        )}
      </div>
      {canCreateFieldNotes && (
        <form
          className="command-field-note-form"
          data-testid="command-field-note-form"
          onSubmit={saveFieldNote}
        >
          <textarea
            data-testid="command-field-note-input"
            onChange={(event) => setNote(event.target.value)}
            placeholder="Add field note"
            value={note}
          />
          <button
            data-testid="command-field-note-save"
            disabled={!note.trim() || isSaving}
            type="submit"
          >
            <MessageSquarePlus size={14} />
            {isSaving ? "Adding…" : "Add note"}
          </button>
        </form>
      )}
      <div className="command-communication-list" data-testid="command-latest-notes">
        {latest.length ? (
          latest.map((item) => (
            <CommunicationHistoryEntry
              isFlashing={item.id === flashCommId}
              item={item}
              key={item.id}
              onManage={onManage}
            />
          ))
        ) : (
          <p className="command-empty-state" data-testid="command-notes-empty">
            No communications recorded.
          </p>
        )}
      </div>
      {historical.length > 0 && (
        <details
          className="command-history-details"
          data-testid="command-communication-history"
        >
          <summary>Communication history · {historical.length}</summary>
          {historical.map((item) => (
            <CommunicationHistoryEntry
              isFlashing={item.id === flashCommId}
              item={item}
              key={item.id}
              onManage={onManage}
            />
          ))}
        </details>
      )}
      <div className="command-activity-heading"><span>Permit Actions</span></div>
      <div
        className="command-communication-list"
        data-testid="command-permit-actions"
      >
        {permitActions.length ? (
          permitActions.map((item) => (
            <article data-testid={`command-permit-action-${item.id}`} key={item.id}>
              <small>
                {item.alert_state || "upcoming"} · {formatTime(item.created_at)}
              </small>
              <strong>{item.title}</strong>
              <p>{item.permit_expires_on || item.due_at || "No due date"}</p>
            </article>
          ))
        ) : (
          <p className="command-empty-state">No Permit Actions.</p>
        )}
      </div>
      <button
        aria-expanded={!activityCollapsed}
        className="command-activity-heading"
        data-testid="command-recent-activity-toggle"
        onClick={() => setActivityCollapsed((prev) => !prev)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: "0.4rem",
          background: "transparent",
          border: "none",
          padding: 0,
          cursor: "pointer",
          textAlign: "left",
          width: "100%",
        }}
        type="button"
      >
        {activityCollapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
        <span>Recent activity ({meaningfulActivity.length})</span>
      </button>
      {!activityCollapsed && (
        <div className="command-activity-list" data-testid="command-recent-activity">
          {meaningfulActivity.length ? (
            meaningfulActivity.slice(0, 4).map((activity) => (
              <article
                data-testid={`command-activity-${activity.id || activity.occurred_at}`}
                key={activity.id || `${activity.occurred_at}-${activity.trigger_type}`}
              >
                <strong>{activityLabel(activity)}</strong>
                <small>{formatTime(activity.occurred_at)}</small>
              </article>
            ))
          ) : (
            <p className="command-empty-state" data-testid="command-activity-empty">
              No recent activity recorded.
            </p>
          )}
        </div>
      )}
    </section>
  );
};